'use client';

import React, { type Ref } from 'react';

import { Body } from '@depop/web-thrift-ui/Typography';
import { LensIcon } from '@depop/web-ui-kit/Icons/LensIcon';
import clsx from 'clsx';
import ReactMarkdown from 'react-markdown';

import styles from './styles.module.css';

import {
  VARIANT_A_IDENTIFIER,
  VARIANT_B_IDENTIFIER,
} from '@/modules/experiments/config';
import { useExperiments } from '@/modules/experiments/useExperiments';
import { type CategorySearchSuggestion } from '@/modules/search/aliases';
import {
  type SearchSuggestionOption,
  SearchSuggestionType,
} from '@/modules/search/types';

interface SearchSuggestionListProps {
  suggestions: SearchSuggestionOption[];
  type: string;
  handleClick: (
    suggestion: SearchSuggestionOption,
    suggestionType: SearchSuggestionType
  ) => void;
  suggestionsRef?: Ref<HTMLUListElement>;
  suggestionType: SearchSuggestionType;
}

function getSearchSuggestionType(suggestion: CategorySearchSuggestion) {
  if ('group' in suggestion && suggestion.group) {
    return SearchSuggestionType.POPULAR_WITH_CATEGORY;
  }
  return null;
}

export function SearchSuggestionList({
  suggestions,
  type,
  handleClick,
  suggestionsRef,
  suggestionType: defaultSuggestionType,
}: SearchSuggestionListProps) {
  const { web_6746_search_for_sellers } = useExperiments([
    'web_6746_search_for_sellers',
  ]);

  const isSearchSellersVariantB =
    web_6746_search_for_sellers === VARIANT_B_IDENTIFIER;

  const isSearchSellersVariantA =
    web_6746_search_for_sellers === VARIANT_A_IDENTIFIER;

  return (
    <ul
      className={clsx(styles.suggestionList)}
      aria-label={`${type} searches`}
      id={`${type}-searches`}
      ref={suggestionsRef}
      // eslint-disable-next-line no-restricted-syntax
      data-testid={`searchBar__${type}--list`}
    >
      {suggestions.map((suggestion, i) => {
        const hasSuggestedFilterLabel = !!suggestion?.suggested_filter_label;
        const suggestionType =
          getSearchSuggestionType(suggestion) ?? defaultSuggestionType;

        const SuggestionText = (
          // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/interactive-supports-focus
          <a
            className={clsx(styles.searchSuggestionText, {
              [styles.searchSuggestionTextVariant]: isSearchSellersVariantB,
            })}
            role="link"
            onClick={() => handleClick(suggestion, suggestionType)}
            tabIndex={-1}
            // eslint-disable-next-line no-restricted-syntax
            data-testid={`searchBar__${type}--${i}`}
            data-value={suggestion.value}
          >
            <Body variant="sm-regular">
              <p className={clsx(styles.textWithEllipsis)}>
                <ReactMarkdown components={{ p: 'span' }}>
                  {suggestion.label}
                </ReactMarkdown>
                {hasSuggestedFilterLabel && (
                  <span className={clsx(styles.categorySuggestionText)}>
                    {' '}
                    {suggestion.suggested_filter_label}
                  </span>
                )}
              </p>
            </Body>
          </a>
        );

        return (
          <li
            key={`${suggestion.value}-${i}`}
            // eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex
            tabIndex={-1}
            className={clsx(styles.searchSuggestion, {
              [styles.searchSuggestionVariantA]: isSearchSellersVariantA,
              [styles.searchSuggestionVariantB]: isSearchSellersVariantB,
            })}
          >
            {isSearchSellersVariantB ? (
              <span className={styles.wrapper}>
                <LensIcon size={16} className={styles.lensIcon} />
                {SuggestionText}
              </span>
            ) : (
              SuggestionText
            )}
          </li>
        );
      })}
    </ul>
  );
}
